import type { InputProps } from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import type { Currency } from '@ps/web-core-enums';
import type { TrackedTranslation, Values } from '@ps/web-core-intl';
import type {
  DropDownOptionValue,
  InjectedValidatorProps,
  SelectOption
} from '@ps/web-shared-components';
import { StarsDropDown, withValidator } from '@ps/web-shared-components';
import type { Errors } from '@ps/web-shared-validators';
import classNames from 'clsx';
import React from 'react';
import { useField, useForm } from 'react-final-form';

import CurrencyIcon from '@swf/components/CurrencyIcon/CurrencyIcon';

import AmountInput from '../AmountInput/AmountInput';
import type { Text } from '../FieldValidationMessage/FieldValidationMessage';
import FieldValidationMessage from '../FieldValidationMessage/FieldValidationMessage';

import { TXTCLI_Currency } from './AmountCurrency.messages.json';
import {
  amountCurrency,
  controls,
  controlsError,
  controlsErrorSuggestion,
  dropdown,
  positionEnd,
  singularCurrencyWrapper
} from './AmountCurrency.scss';

export interface Props extends InputProps, InjectedValidatorProps {
  amountFieldName: string;
  currencyFieldName: string;
  defaultCurrency: Currency;
  extension?: React.ReactNode;
  includeCurrencies: Currency[];
  label: TrackedTranslation;
  maxInputAmount?: number;
  normalize?: boolean;
  onAmountBlur?: () => void;
  onAmountFocus?: () => void;
  onCurrencySelect?: (currency: DropDownOptionValue | undefined) => void;
  resetAmountOnCurrencyChange?: boolean;
  staticMessage?: Text;
  values?: Values;
  withPlaceholder?: boolean;
  optionsWithBalances?: SelectOption[];
  autofocus?: boolean;
  disabled?: boolean;
  inputClass?: string;
  errorClass?: string;
}

const AmountCurrency = withValidator<Props>(
  ({
    amountFieldName,
    currencyFieldName,
    defaultCurrency,
    extension,
    includeCurrencies,
    label,
    maxInputAmount,
    normalize,
    onAmountBlur,
    onAmountFocus,
    onCurrencySelect,
    resetAmountOnCurrencyChange = true,
    staticMessage,
    validationErrors,
    validator,
    values,
    withPlaceholder,
    optionsWithBalances,
    autofocus = false,
    disabled = false,
    inputClass,
    errorClass
  }: Props) => {
    const {
      input: { value: currency }
    } = useField(currencyFieldName, {
      subscription: { value: true },
      initialValue: defaultCurrency
    });

    const form = useForm();
    const currencyOptions: SelectOption[] = includeCurrencies.map((currency) => ({
      text: currency,
      value: currency
    }));

    const [defaultOption] = currencyOptions.filter(({ value }) => value === currency);
    return (
      <div className={amountCurrency}>
        <div className={controls}>
          <AmountInput
            className={inputClass}
            disabled={disabled}
            fieldName={amountFieldName}
            currency={currency}
            inputProps={{
              min: 0,
              step: 0.01,
              inputMode: 'decimal'
            }}
            normalize={normalize}
            onAmountBlur={() => onAmountBlur?.()}
            onFocus={() => onAmountFocus?.()}
            validator={validator}
            validationErrors={validationErrors}
            label={label}
            values={values}
            withPlaceholder={withPlaceholder}
            hasExtension={!!extension}
            endAdornment={
              <InputAdornment
                position="end"
                classes={{
                  positionEnd
                }}
              >
                <StarsDropDown
                  id={currencyFieldName}
                  label={TXTCLI_Currency}
                  className={classNames(dropdown, {
                    [singularCurrencyWrapper]: includeCurrencies.length <= 1
                  })}
                  options={optionsWithBalances || currencyOptions}
                  defaultOption={defaultOption}
                  disabled={includeCurrencies.length <= 1}
                  icon={<CurrencyIcon currency={currency} variant="dropdown" />}
                  onSelect={({ value }) => {
                    if (value !== currency) {
                      resetAmountOnCurrencyChange && form.change(amountFieldName);
                      onCurrencySelect && onCurrencySelect(value);
                    }
                  }}
                  hideLabel
                />
              </InputAdornment>
            }
            maxInputAmount={maxInputAmount}
            autoFocus={autofocus}
          />
        </div>
        <FieldValidationMessage
          errors={validationErrors as Errors}
          fieldName={amountFieldName}
          className={classNames(
            controlsError,
            { [controlsErrorSuggestion]: !!extension },
            errorClass
          )}
          staticMessage={staticMessage}
        />
        {extension}
      </div>
    );
  }
);

export default AmountCurrency;
